import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { WorkplaceColorSpectrumBlock, SpectrumColor, ThemeTokens } from '@course-authoring/shared-types';
import {
  isRichTextContentEmpty,
  useAccountConfig,
  useTheme,
  useResultsHidden,
} from '@course-authoring/shared-types';
import { useTranslation } from '@course-authoring/i18n';

import { usePrefersReducedMotion } from './hooks/usePrefersReducedMotion';
import { useSubmitRevealTransition } from './hooks/useSubmitRevealTransition';
import { useCommittedQuestionNarrationOutcome } from './hooks/useCommittedQuestionNarrationOutcome';
import { createSubmitRevealStageStyles } from './hooks/submitRevealStageStyles';
import { SubmitRevealButton } from './SubmitRevealButton';
import {
  createRichTextContentResetStyles,
  createRichTextRendererExtensions,
  safeGenerateNonEmptyRichTextHTML,
  safeGenerateRichTextHTML,
} from './richTextRenderer';
import { createContentBlockStyles } from './styles/contentBlocks';
import { getContentBlockHeadingTypography } from './styles/titleTypography';
import { workplaceColorSpectrumEmojiAssets } from './workplaceColorSpectrumEmojiAssets';
import { useCardBackground, useSectionSurface } from './SectionSurfaceContext';
import { useIsMobileViewport } from './hooks/useIsMobileViewport';
import { useContainerWidth } from './hooks/useContainerWidth';
import { getStandardInteractionWidth } from './interactionLayout';
import { ThankYouMessage } from './ThankYouMessage';
import type { QuestionNarrationOutcomeHandler } from './narrationEvents';
import { ResponseThresholdEmptyState } from './ResponseThresholdEmptyState';
import { localizeResultsDistribution } from './resultsLocalization';
import { RichTextContent } from './RichTextContent';

type SegmentGeometry = {
  color: SpectrumColor;
  startAngle: number;
  endAngle: number;
  centerAngle: number;
};

type HalfDonutSegment = {
  color: SpectrumColor;
  label: string;
  fill: string;
  legendFill: string;
  startAngle: number;
  endAngle: number;
};

export type WorkplaceColorSpectrumResultsDistribution = {
  id: 'my-org' | 'global';
  responseCount: number | string;
  values: Record<SpectrumColor, number>;
};

type LocalizedWorkplaceColorSpectrumResultsDistribution = WorkplaceColorSpectrumResultsDistribution & {
  title: string;
  responsesLabel: string;
};

const COLOR_KEYS: SpectrumColor[] = ['green', 'yellow', 'orange', 'red'];

// Color values come straight from tokens.wcs.* — pure helpers take the
// derived meta as a parameter so nothing here can drift from themeTokens.ts.
// `legendFill` is a darker variant for legend-swatch visibility on white;
// only `yellow` differs from `fill` today. See feedback-to-claude-design.md.
type WcsColorMeta = Record<SpectrumColor, { label: string; fill: string; legendFill: string }>;

const WCS_COLOR_TRANSLATION_KEYS = {
  green: {
    label: 'workplace_color_spectrum.colors.green.label',
    title: 'workplace_color_spectrum.colors.green.title',
    description: 'workplace_color_spectrum.colors.green.description',
  },
  yellow: {
    label: 'workplace_color_spectrum.colors.yellow.label',
    title: 'workplace_color_spectrum.colors.yellow.title',
    description: 'workplace_color_spectrum.colors.yellow.description',
  },
  orange: {
    label: 'workplace_color_spectrum.colors.orange.label',
    title: 'workplace_color_spectrum.colors.orange.title',
    description: 'workplace_color_spectrum.colors.orange.description',
  },
  red: {
    label: 'workplace_color_spectrum.colors.red.label',
    title: 'workplace_color_spectrum.colors.red.title',
    description: 'workplace_color_spectrum.colors.red.description',
  },
} as const satisfies Record<SpectrumColor, Record<'label' | 'title' | 'description', string>>;

const getWcsColorMeta = (
  wcs: ThemeTokens['wcs'],
  labels: Record<SpectrumColor, string>,
): WcsColorMeta => ({
  green: { label: labels.green, fill: wcs.green, legendFill: wcs.green },
  yellow: { label: labels.yellow, fill: wcs.yellow, legendFill: wcs.yellowLegend },
  orange: { label: labels.orange, fill: wcs.orange, legendFill: wcs.orange },
  red: { label: labels.red, fill: wcs.red, legendFill: wcs.red },
});

const QUESTION_CENTER_X = 280;
const QUESTION_CENTER_Y = 280;
const QUESTION_OUTER_RADIUS = 205;
const QUESTION_INNER_RADIUS = 140;
const LABEL_OFFSET_SIDE = 38;
const LABEL_OFFSET_TOP = 60;

const RESULTS_CHART_WIDTH = 360;
const RESULTS_CHART_HEIGHT = 175;
const RESULTS_CHART_CENTER_X = 180;
const RESULTS_CHART_CENTER_Y = 160;
const RESULTS_CHART_OUTER_RADIUS = 138;
const RESULTS_CHART_INNER_RADIUS = 84;
const SPECTRUM_SUBMIT_TRANSITION_MS = 180;
const SPECTRUM_RESULTS_ENTER_DELAY_MS = 20;
const SPECTRUM_RESULTS_ENTER_MS = 220;
const RESULTS_CHART_ANIMATION_DELAY_MS = 80;
const RESULTS_CHART_ANIMATION_MS = 900;

// Responsive breakpoints are content-width based (measured block width via
// useContainerWidth), not viewport — each sub-part switches at the block width
// where it actually gets tight, so the authoring preview (narrowed by the
// editor's assignment panel) and the exported player behave identically.
// Values are tuned by eye against the design.
// Below these block widths the gauge's outer labels step down (they are
// fixed-size and would otherwise collide as the fluid gauge narrows). Two 2px
// steps: 18/16 below the first, 16/14 below the second, 14/12 below the third.
const WCS_GAUGE_COMPACT_WIDTH = 520;
const WCS_GAUGE_COMPACT_WIDTH_2 = 475;
const WCS_GAUGE_COMPACT_WIDTH_3 = 350;
// Below this block width the two results charts stack vertically (each full
// width), so their text returns to full size.
const WCS_CHARTS_STACK_WIDTH = 680;
// Below this block width the results chart text (title/responses/legend) steps
// down in 2px steps while the two charts are still side-by-side but getting
// cramped (the legend text starts crowding the chart-card edges around here).
// Only applies above the stack width — once stacked each chart has room again.
// Step 1: label 18→16, value 20→18. Step 2: label 16→14, value 18→16.
// Kept clear of the ~878 card width the layout lands on when the learner nav
// collapses (~1100px browser), so the collapse doesn't drop straight into a step.
const WCS_CHART_TEXT_COMPACT_WIDTH = 840;
const WCS_CHART_TEXT_COMPACT_WIDTH_2 = 785;
// Below this card width the results card's side padding narrows — the desktop
// 67px inline padding is too much on phones and crushes the content.
const WCS_RESULTS_PADDING_COMPACT_WIDTH = 560;
// When the charts are stacked (full width each) the legend has room, so it only
// needs to shrink again at true phone widths. Two 2px steps: 16/18 below the
// first, 14/16 below the second.
const WCS_LEGEND_STACKED_COMPACT_WIDTH = 430;
const WCS_LEGEND_STACKED_COMPACT_WIDTH_2 = 365;

const SEGMENT_GEOMETRY: readonly SegmentGeometry[] = [
  {
    color: 'green',
    startAngle: 180,
    endAngle: 225,
    centerAngle: 202.5,
  },
  {
    color: 'yellow',
    startAngle: 225,
    endAngle: 270,
    centerAngle: 247.5,
  },
  {
    color: 'orange',
    startAngle: 270,
    endAngle: 315,
    centerAngle: 292.5,
  },
  {
    color: 'red',
    startAngle: 315,
    endAngle: 360,
    centerAngle: 337.5,
  },
] as const;

const GEOMETRY_BY_COLOR: Record<SpectrumColor, SegmentGeometry> = {
  green: SEGMENT_GEOMETRY[0] as SegmentGeometry,
  yellow: SEGMENT_GEOMETRY[1] as SegmentGeometry,
  orange: SEGMENT_GEOMETRY[2] as SegmentGeometry,
  red: SEGMENT_GEOMETRY[3] as SegmentGeometry,
};

// Placeholder data shown until real aggregate results are available; hosts
// can inject real data via the resultsDistributions prop.
const PLACEHOLDER_RESULTS_DISTRIBUTIONS: readonly WorkplaceColorSpectrumResultsDistribution[] = [
  {
    id: 'my-org',
    responseCount: 3_850,
    values: {
      green: 14,
      yellow: 70,
      orange: 14,
      red: 2,
    },
  },
  {
    id: 'global',
    responseCount: 240_000,
    values: {
      green: 5,
      yellow: 56,
      orange: 37,
      red: 2,
    },
  },
] as const;

const PLACEHOLDER_RESULTS_WITHOUT_ORG = PLACEHOLDER_RESULTS_DISTRIBUTIONS.filter(
  (d) => d.id !== 'my-org',
);

const DEFAULT_TITLE = 'The Workplace Color Spectrum\u00AE';
const DEFAULT_DESCRIPTION = "Rate the manager\u2019s initial reaction to the employee\u2019s report.";

function polarToCartesian(
  centerX: number,
  centerY: number,
  radius: number,
  angleInDegrees: number,
): { x: number; y: number } {
  const angleInRadians = (angleInDegrees * Math.PI) / 180;
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
}

function buildRingSegmentPath(
  centerX: number,
  centerY: number,
  outerRadius: number,
  innerRadius: number,
  startAngle: number,
  endAngle: number,
): string {
  const outerStart = polarToCartesian(centerX, centerY, outerRadius, startAngle);
  const outerEnd = polarToCartesian(centerX, centerY, outerRadius, endAngle);
  const innerEnd = polarToCartesian(centerX, centerY, innerRadius, endAngle);
  const innerStart = polarToCartesian(centerX, centerY, innerRadius, startAngle);
  const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;

  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerEnd.x} ${innerEnd.y}`,
    `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${innerStart.x} ${innerStart.y}`,
    'Z',
  ].join(' ');
}

function buildSegmentPath(segment: SegmentGeometry): string {
  return buildRingSegmentPath(
    QUESTION_CENTER_X,
    QUESTION_CENTER_Y,
    QUESTION_OUTER_RADIUS,
    QUESTION_INNER_RADIUS,
    segment.startAngle,
    segment.endAngle,
  );
}

function buildHalfDonutSegments(
  values: Record<SpectrumColor, number>,
  colorMeta: WcsColorMeta,
  progress = 1,
): HalfDonutSegment[] {
  const clampedProgress = Math.min(Math.max(progress, 0), 1);
  let remainingPercent = clampedProgress * 100;
  let angleCursor = 180;

  return COLOR_KEYS.flatMap((color) => {
    const filledPercent = Math.max(0, Math.min(values[color], remainingPercent));
    const startAngle = angleCursor;
    const endAngle = startAngle + (filledPercent / 100) * 180;
    angleCursor = endAngle;
    remainingPercent = Math.max(0, remainingPercent - values[color]);

    if (filledPercent <= 0) {
      return [];
    }

    return [{
      color,
      label: colorMeta[color].label,
      fill: colorMeta[color].fill,
      legendFill: colorMeta[color].legendFill,
      startAngle,
      endAngle,
    }];
  });
}

function buildHalfDonutPath(segment: HalfDonutSegment): string {
  return buildRingSegmentPath(
    RESULTS_CHART_CENTER_X,
    RESULTS_CHART_CENTER_Y,
    RESULTS_CHART_OUTER_RADIUS,
    RESULTS_CHART_INNER_RADIUS,
    segment.startAngle,
    segment.endAngle,
  );
}

const POINTER_TIP_RADIUS = (QUESTION_INNER_RADIUS + QUESTION_OUTER_RADIUS) / 2;
const POINTER_BASE_HALF_WIDTH = 14;

function formatSvgCoordinate(value: number): string {
  return `${Number(value.toFixed(3))}`;
}

function buildPointerPath(angleInDegrees: number): string {
  const tip = polarToCartesian(
    QUESTION_CENTER_X,
    QUESTION_CENTER_Y,
    POINTER_TIP_RADIUS,
    angleInDegrees,
  );
  const baseRight = polarToCartesian(
    QUESTION_CENTER_X,
    QUESTION_CENTER_Y,
    POINTER_BASE_HALF_WIDTH,
    angleInDegrees + 90,
  );
  const baseLeft = polarToCartesian(
    QUESTION_CENTER_X,
    QUESTION_CENTER_Y,
    POINTER_BASE_HALF_WIDTH,
    angleInDegrees - 90,
  );

  return [
    `M ${formatSvgCoordinate(tip.x)} ${formatSvgCoordinate(tip.y)}`,
    `L ${formatSvgCoordinate(baseRight.x)} ${formatSvgCoordinate(baseRight.y)}`,
    `L ${formatSvgCoordinate(baseLeft.x)} ${formatSvgCoordinate(baseLeft.y)}`,
    'Z',
  ].join(' ');
}

const contentResetStyles = createRichTextContentResetStyles('spectrum-definition-content');

export interface WorkplaceColorSpectrumRendererProps {
  block: WorkplaceColorSpectrumBlock;
  className?: string;
  style?: React.CSSProperties;
  onInteractionComplete?: () => void;
  /**
   * Fires once on submit with the bank option id whose label matches the
   * selected color; not fired when the block carries no resolved options
   * (NA-178 response logging).
   */
  onAnswerSubmit?: (answer: { optionId: string }) => void;
  onNarrationOutcomeCommitted?: QuestionNarrationOutcomeHandler;
  /**
   * Aggregate results to display; defaults to placeholder data. `'below-
   * threshold'` (NA-72) renders the "not enough responses" empty state
   * instead of a distributions array; `'pending'` renders a lightweight
   * loading message while the real results fetch is in flight.
   */
  resultsDistributions?: readonly WorkplaceColorSpectrumResultsDistribution[] | 'below-threshold' | 'pending';
  /**
   * Whether the player has found a prior recorded answer for this block on
   * bootstrap (NA-319). `'loading'` renders a lightweight placeholder in
   * place of both the question and results views, so a learner can't submit
   * a new answer while the player is still checking. Defaults to
   * `'unanswered'` (today's behavior) for any caller that doesn't opt in,
   * e.g. authoring previews.
   */
  priorAnswerStatus?: 'loading' | 'answered' | 'unanswered';
  /** The learner's previously recorded `answerOptionId`, present only when `priorAnswerStatus === 'answered'`. */
  priorAnswerOptionId?: string;
}

/** The SpectrumColor for a bank answerOptionId (reverse of the submit mapping), or null if not found/absent. */
function getSpectrumColorFromOptionId(
  options: WorkplaceColorSpectrumBlock['options'],
  answerOptionId: string | undefined
): SpectrumColor | null {
  if (!options || !answerOptionId) return null;
  const option = options.find((o) => o.id === answerOptionId);
  if (!option) return null;
  const color = option.label.trim().toLowerCase();
  return (COLOR_KEYS as string[]).includes(color) ? (color as SpectrumColor) : null;
}

export function WorkplaceColorSpectrumRenderer({
  block,
  className,
  style,
  onInteractionComplete,
  onAnswerSubmit,
  onNarrationOutcomeCommitted,
  resultsDistributions: resultsDistributionsInput,
  priorAnswerStatus = 'unanswered',
  priorAnswerOptionId,
}: WorkplaceColorSpectrumRendererProps): React.ReactElement {
  const { t, i18n } = useTranslation();
  const resultsHidden = useResultsHidden(block.hideResults);
  const { hideOrg } = useAccountConfig();
  // Placeholder data (Preview) must respect the account's My Org toggle (NA-344).
  const resultsDistributionsProp =
    resultsDistributionsInput ??
    (hideOrg ? PLACEHOLDER_RESULTS_WITHOUT_ORG : PLACEHOLDER_RESULTS_DISTRIBUTIONS);
  const isBelowResponseThreshold = resultsDistributionsProp === 'below-threshold';
  const isResultsPending = resultsDistributionsProp === 'pending';
  const resultsDistributions = useMemo<readonly LocalizedWorkplaceColorSpectrumResultsDistribution[]>(() => {
    if (typeof resultsDistributionsProp === 'string') return [];
    const locale = i18n.resolvedLanguage ?? i18n.language;
    return resultsDistributionsProp.map((distribution) => {
      return {
        ...distribution,
        ...localizeResultsDistribution(t, locale, distribution, {
          myOrgTitle: 'results.my_org',
          globalTitle: 'results.global',
          responsesOne: 'results.responses_title_one',
          responsesOther: 'results.responses_title_other',
        }),
      };
    });
  }, [i18n.language, i18n.resolvedLanguage, resultsDistributionsProp, t]);
  const { tokens } = useTheme();
  const isMobile = useIsMobileViewport(tokens.layout.mobileBreakpoint);
  // General prose (title, description, definition, results text, chart titles)
  // follows the standard viewport tiers (768 / 576) per NA-205 + NA-227 — the
  // gauge labels and chart legend are hand-tuned on card width instead.
  const isSmallMobile = useIsMobileViewport(tokens.layout.smallMobileBreakpoint);
  const containerRef = useRef<HTMLDivElement>(null);
  const containerWidth = useContainerWidth(containerRef);
  // Gauge outer labels step down in 2px increments as the gauge narrows — a
  // single jump straight to the phone size is too dramatic. 0 = full (20/18),
  // 1 = 18/16, 2 = 16/14, 3 = 14/12.
  const gaugeStep =
    containerWidth === undefined
      ? 0
      : containerWidth < WCS_GAUGE_COMPACT_WIDTH_3
        ? 3
        : containerWidth < WCS_GAUGE_COMPACT_WIDTH_2
          ? 2
          : containerWidth < WCS_GAUGE_COMPACT_WIDTH
            ? 1
            : 0;
  const isResultsPaddingCompact =
    containerWidth !== undefined && containerWidth < WCS_RESULTS_PADDING_COMPACT_WIDTH;
  // Phones always stack (matches the mobile spec). Below the 768 mobile
  // breakpoint the block switches to 90% interaction width, which widens the
  // card as the browser narrows — without this floor that would pop the charts
  // back to side-by-side just under 768.
  const isChartsStacked =
    isMobile || (containerWidth !== undefined && containerWidth < WCS_CHARTS_STACK_WIDTH);
  // Legend font steps down when its columns get cramped. 0 = full, 1 = first 2px
  // step (16/18), 2 = second 2px step (14/16). Side-by-side: two steps at
  // 840/785 (the two charts share the width). Stacked: each chart is full width,
  // so the legend only needs to shrink again at true phone widths (below 430).
  const legendStep =
    containerWidth === undefined
      ? 0
      : isChartsStacked
        ? containerWidth < WCS_LEGEND_STACKED_COMPACT_WIDTH_2
          ? 2
          : containerWidth < WCS_LEGEND_STACKED_COMPACT_WIDTH
            ? 1
            : 0
        : containerWidth < WCS_CHART_TEXT_COMPACT_WIDTH_2
          ? 2
          : containerWidth < WCS_CHART_TEXT_COMPACT_WIDTH
            ? 1
            : 0;
  const cardBackground = useCardBackground();
  const sectionSurface = useSectionSurface();
  const styles = useMemo(() => createContentBlockStyles(tokens), [tokens]);
  const colorLabels = useMemo<Record<SpectrumColor, string>>(() => ({
    green: t(WCS_COLOR_TRANSLATION_KEYS.green.label),
    yellow: t(WCS_COLOR_TRANSLATION_KEYS.yellow.label),
    orange: t(WCS_COLOR_TRANSLATION_KEYS.orange.label),
    red: t(WCS_COLOR_TRANSLATION_KEYS.red.label),
  }), [t]);
  const colorMeta = useMemo(
    () => getWcsColorMeta(tokens.wcs, colorLabels),
    [colorLabels, tokens.wcs],
  );
  const prefersReducedMotion = usePrefersReducedMotion();
  const extensions = useMemo(() => createRichTextRendererExtensions(), []);
  const [hoveredColor, setHoveredColor] = useState<SpectrumColor | null>(null);
  const initialPriorColor =
    priorAnswerStatus === 'answered'
      ? getSpectrumColorFromOptionId(block.options, priorAnswerOptionId)
      : null;
  const [selectedColor, setSelectedColor] = useState<SpectrumColor | null>(() => initialPriorColor);
  const [interactionComplete, setInteractionComplete] = useState(() => initialPriorColor !== null);
  // Chart reveal is animated imperatively via these refs (SVG attribute
  // writes per frame) instead of per-frame React state, so the 60fps loop
  // doesn't re-render the whole component.
  const chartPathRefs = useRef(new Map<string, SVGPathElement | null>());
  const chartBaselineRefs = useRef(new Map<string, SVGLineElement | null>());
  const interactionWidth = getStandardInteractionWidth(tokens, isMobile);
  const {
    transitionPhase,
    isQuestionStageVisible,
    isResultsStageVisible,
    isSubmitting,
    startTransition,
    revealImmediately,
  } = useSubmitRevealTransition({
    prefersReducedMotion,
    onInteractionComplete,
    submitTransitionMs: SPECTRUM_SUBMIT_TRANSITION_MS,
    resultsEnterDelayMs: SPECTRUM_RESULTS_ENTER_DELAY_MS,
    initialPhase: initialPriorColor !== null ? 'results' : 'question',
  });

  // NA-319: the bootstrap prior-answers fetch normally resolves after this
  // block's first mount, not before it - so the lazy useState/initialPhase
  // seeding above only covers the (uncommon) case where it's already known
  // at mount. This effect covers the common case: the prop transitions from
  // 'loading' to 'answered' after mount.
  useEffect(() => {
    if (priorAnswerStatus !== 'answered' || selectedColor !== null) return;
    const color = getSpectrumColorFromOptionId(block.options, priorAnswerOptionId);
    if (color === null) return;
    setSelectedColor(color);
    setInteractionComplete(true);
    revealImmediately();
  }, [priorAnswerStatus, priorAnswerOptionId, block.options, selectedColor, revealImmediately]);

  const activeColor = hoveredColor ?? selectedColor;
  const activeGeometry = activeColor ? GEOMETRY_BY_COLOR[activeColor] : null;
  const pointerAngle = activeGeometry?.centerAngle ?? 270;
  const pointerPath = useMemo(() => buildPointerPath(pointerAngle), [pointerAngle]);
  const correctAnswer: SpectrumColor = block.correctAnswer ?? 'yellow';
  const submittedColor = selectedColor ?? correctAnswer;
  const shouldShowResults = isResultsStageVisible && selectedColor !== null;
  useCommittedQuestionNarrationOutcome({
    outcomeKey: shouldShowResults && selectedColor !== null
      ? `${block.id}:${selectedColor}:${resultsHidden ? 'hidden' : 'visible'}`
      : null,
    outcome: shouldShowResults ? 'submitted' : null,
    presentation: shouldShowResults
      ? (resultsHidden ? 'hidden-results' : 'visible-results')
      : null,
    onCommitted: onNarrationOutcomeCommitted,
  });

  const titleLevel = block.titleLevel ?? 'h2';
  const titleText = block.title ?? DEFAULT_TITLE;

  const hasBlockDescription =
    block.description != null && !isRichTextContentEmpty(block.description);
  const descriptionHtml = useMemo(() => {
    if (hasBlockDescription && block.description) {
      return safeGenerateRichTextHTML(block.description, extensions);
    }
    return `<p>${DEFAULT_DESCRIPTION}</p>`;
  }, [block.description, extensions, hasBlockDescription]);

  const getColorSubtitle = useCallback(
    (color: SpectrumColor): string => t(WCS_COLOR_TRANSLATION_KEYS[color].title),
    [t],
  );

  const getColorDefinition = useCallback(
    (color: SpectrumColor): string => t(WCS_COLOR_TRANSLATION_KEYS[color].description),
    [t],
  );

  // The fixed color description is pre-submit copy (shown below the gauge);
  // without an authored explanation the results area stays blank.
  const resultExplanationContent = useMemo(() => {
    if (
      block.correctAnswerExplanation &&
      !isRichTextContentEmpty(block.correctAnswerExplanation)
    ) {
      return block.correctAnswerExplanation;
    }

    return null;
  }, [block.correctAnswerExplanation]);

  const resultExplanationHtml = useMemo(() => {
    if (!resultExplanationContent) {
      return null;
    }

    return safeGenerateNonEmptyRichTextHTML(resultExplanationContent, extensions);
  }, [extensions, resultExplanationContent]);

  // Writes the chart geometry for a given reveal progress (0..1) straight to
  // the SVG elements. The JSX renders the final (progress = 1) geometry; this
  // only mutates attributes, so React never re-renders during the animation.
  const applyChartProgress = useCallback(
    (progress: number) => {
      const baselineStartX = RESULTS_CHART_CENTER_X - RESULTS_CHART_OUTER_RADIUS;
      for (const distribution of resultsDistributions) {
        const segmentsByColor = new Map(
          buildHalfDonutSegments(distribution.values, colorMeta, progress).map(
            (segment) => [segment.color, segment] as const,
          ),
        );
        for (const color of COLOR_KEYS) {
          const path = chartPathRefs.current.get(`${distribution.id}:${color}`);
          if (!path) continue;
          const segment = segmentsByColor.get(color);
          if (segment) {
            path.setAttribute('d', buildHalfDonutPath(segment));
            path.removeAttribute('display');
          } else {
            path.setAttribute('display', 'none');
          }
        }
        const baseline = chartBaselineRefs.current.get(distribution.id);
        if (baseline) {
          baseline.setAttribute(
            'x2',
            String(baselineStartX + RESULTS_CHART_OUTER_RADIUS * 2 * progress),
          );
        }
      }
    },
    [resultsDistributions, colorMeta],
  );

  // Layout effect so the reset to progress 0 lands before paint — otherwise
  // the fully revealed chart would flash before the animation starts.
  useLayoutEffect(() => {
    if (!shouldShowResults) {
      return;
    }

    if (prefersReducedMotion || typeof window === 'undefined') {
      applyChartProgress(1);
      return;
    }

    if (transitionPhase !== 'results') {
      applyChartProgress(0);
      return;
    }

    const requestFrame =
      typeof window.requestAnimationFrame === 'function'
        ? window.requestAnimationFrame.bind(window)
        : (callback: FrameRequestCallback) => window.setTimeout(() => callback(Date.now()), 16);
    const cancelFrame =
      typeof window.cancelAnimationFrame === 'function'
        ? window.cancelAnimationFrame.bind(window)
        : window.clearTimeout.bind(window);

    let frameId = 0;
    let delayTimer = 0;
    let animationStart: number | null = null;

    applyChartProgress(0);

    const animate = (timestamp: number) => {
      if (animationStart === null) {
        animationStart = timestamp;
      }

      const rawProgress = Math.min((timestamp - animationStart) / RESULTS_CHART_ANIMATION_MS, 1);
      const easedProgress = 1 - Math.pow(1 - rawProgress, 3);
      applyChartProgress(easedProgress);

      if (rawProgress < 1) {
        frameId = requestFrame(animate);
      }
    };

    delayTimer = window.setTimeout(() => {
      frameId = requestFrame(animate);
    }, RESULTS_CHART_ANIMATION_DELAY_MS);

    return () => {
      window.clearTimeout(delayTimer);
      cancelFrame(frameId);
    };
  }, [applyChartProgress, prefersReducedMotion, shouldShowResults, transitionPhase]);

  const setHovered = useCallback((color: SpectrumColor | null) => {
    setHoveredColor(color);
  }, []);

  const setSelected = useCallback((color: SpectrumColor) => {
    setSelectedColor(color);
  }, []);

  const handleSubmit = useCallback(() => {
    if (!selectedColor || interactionComplete) {
      return;
    }

    // A bank option's label lowercases to its SpectrumColor (the same match
    // the server uses for correctAnswer). Blocks without resolved options
    // simply don't emit.
    const answerOptionId = block.options?.find(
      (option) => option.label.trim().toLowerCase() === selectedColor,
    )?.id;
    if (answerOptionId) {
      onAnswerSubmit?.({ optionId: answerOptionId });
    }

    setHoveredColor(null);
    setInteractionComplete(true);
    startTransition();
  }, [interactionComplete, selectedColor, startTransition, block.options, onAnswerSubmit]);

  const submitDisabled = selectedColor === null;
  const submitAccentColor =
    selectedColor === null
      ? tokens.backgrounds.inverse
      : selectedColor === 'yellow'
        ? colorMeta.yellow.legendFill
        : colorMeta[selectedColor].fill;
  const submitStatusTextColor =
    selectedColor === 'yellow' ? tokens.text.primary : tokens.text.onInverse;
  const { questionStage: questionStageStyle, resultsStage: resultsStageStyle } =
    createSubmitRevealStageStyles({
      prefersReducedMotion,
      transitionPhase,
      submitTransitionMs: SPECTRUM_SUBMIT_TRANSITION_MS,
      resultsEnterMs: SPECTRUM_RESULTS_ENTER_MS,
    });

  const shouldDimUnselectedLabels = selectedColor !== null;
  const focusedLabelColor = hoveredColor ?? selectedColor;

  const getLabelPositionStyles = useCallback(
    (segment: SegmentGeometry): React.CSSProperties => {
      const isTopLabel = segment.centerAngle >= 225 && segment.centerAngle <= 315;
      const labelOffset = isTopLabel ? LABEL_OFFSET_TOP : LABEL_OFFSET_SIDE;
      const labelRadius = QUESTION_OUTER_RADIUS + labelOffset;
      const labelPos = polarToCartesian(
        QUESTION_CENTER_X,
        QUESTION_CENTER_Y,
        labelRadius,
        segment.centerAngle,
      );

      let transform: string;
      let alignItems: string;
      let textAlign: string;

      if (segment.centerAngle < 225) {
        transform = 'translateX(-100%)';
        alignItems = 'flex-end';
        textAlign = 'right';
      } else if (segment.centerAngle > 315) {
        transform = 'none';
        alignItems = 'flex-start';
        textAlign = 'left';
      } else {
        transform = 'translateX(-50%)';
        alignItems = 'center';
        textAlign = 'center';
      }

      return {
        left: `${(labelPos.x / 560) * 100}%`,
        top: `${(labelPos.y / 320) * 100}%`,
        transform,
        alignItems: alignItems as React.CSSProperties['alignItems'],
        textAlign: textAlign as React.CSSProperties['textAlign'],
      };
    },
    [],
  );

  // NA-227: shared three-tier content-block heading resolution — desktop scale,
  // <768 mobile scale (h2 40→30, h3 30→24), and ≤576 narrow-phone steps
  // (h2→26, h3→20) — with the size-keyed line-height rule (≥26px → 1.25).
  const titleTypography = useMemo(
    () => getContentBlockHeadingTypography(tokens, titleLevel, { isMobile, isSmallMobile }),
    [isMobile, isSmallMobile, titleLevel, tokens],
  );

  const titleStyle: React.CSSProperties = useMemo(
    () => ({
      ...styles.workplaceSpectrumTitle,
      fontSize: titleTypography.size,
      fontWeight: titleTypography.weight,
      lineHeight: titleTypography.lineHeight,
    }),
    [styles.workplaceSpectrumTitle, titleTypography],
  );

  // Content-width step-downs (see WCS_*_WIDTH). Fixed-size gauge labels shrink
  // as the fluid gauge narrows; chart legend text shrinks while the charts are
  // still side-by-side but cramped.
  const labelTitleStyle =
    gaugeStep > 0
      ? {
        ...styles.workplaceSpectrumLabelTitle,
        fontSize:
          gaugeStep === 3
            ? tokens.typography.sizes.sm
            : gaugeStep === 2
              ? tokens.typography.sizes.base
              : tokens.typography.sizes.ui,
        lineHeight: tokens.typography.lineHeights.tight,
      }
      : styles.workplaceSpectrumLabelTitle;
  const labelSubtitleStyle =
    gaugeStep > 0
      ? {
        ...styles.workplaceSpectrumLabelSubtitle,
        fontSize:
          gaugeStep === 3
            ? tokens.typography.sizes.xs
            : gaugeStep === 2
              ? tokens.typography.sizes.sm
              : tokens.typography.sizes.base,
        lineHeight: tokens.typography.lineHeights.tight,
      }
      : styles.workplaceSpectrumLabelSubtitle;
  const legendLabelStyle =
    legendStep > 0
      ? {
        ...styles.workplaceSpectrumResultsLegendLabel,
        fontSize:
          legendStep === 2 ? tokens.typography.sizes.sm : tokens.typography.sizes.base,
      }
      : styles.workplaceSpectrumResultsLegendLabel;
  const legendValueStyle =
    legendStep > 0
      ? {
        ...styles.workplaceSpectrumResultsLegendValue,
        fontSize:
          legendStep === 2 ? tokens.typography.sizes.base : tokens.typography.sizes.ui,
      }
      : styles.workplaceSpectrumResultsLegendValue;
  const chartsRowStyle: React.CSSProperties = isChartsStacked
    ? {
      ...styles.workplaceSpectrumResultsChartsRow,
      flexDirection: 'column',
      flexWrap: 'nowrap',
      alignItems: 'center',
    }
    : // Row mode: nowrap so the charts stay side-by-side and shrink to equal
    // halves until WCS_CHARTS_STACK_WIDTH — never wrapping on their own, so
    // the stack point (and the small-legend band above it) is deterministic.
    { ...styles.workplaceSpectrumResultsChartsRow, flexWrap: 'nowrap' };
  const chartCardStyle: React.CSSProperties = isChartsStacked
    ? { ...styles.workplaceSpectrumResultsChartCard, flex: '0 0 auto', width: '100%', maxWidth: '420px' }
    : { ...styles.workplaceSpectrumResultsChartCard, flex: '1 1 0', minWidth: 0 };
  // Correct-answer cluster stacks together with the charts: the tile centers
  // above the explanation, which goes full-width (left-aligned). Also removes
  // the fixed 238px tile from beside the text, which was pinning the card's
  // min-content (and blocking it from shrinking below ~680).
  const correctAnswerClusterStyle: React.CSSProperties = isChartsStacked
    ? {
      ...styles.workplaceSpectrumResultsCorrectAnswerCluster,
      flexDirection: 'column',
      flexWrap: 'nowrap',
      alignItems: 'center',
    }
    : styles.workplaceSpectrumResultsCorrectAnswerCluster;
  const resultsCardStyle: React.CSSProperties = isResultsPaddingCompact
    ? { ...styles.workplaceSpectrumResultsCard, paddingInline: tokens.spacing.xl }
    : styles.workplaceSpectrumResultsCard;

  // NA-205: general prose steps 20/18px → 16px at ≤576. Applies to the
  // description, gauge definition, results text, and chart titles.
  const stepBody = (base: React.CSSProperties): React.CSSProperties =>
    isSmallMobile ? { ...base, fontSize: tokens.typography.mobile.body.size } : base;
  const subtitleStyle = stepBody(styles.workplaceSpectrumSubtitle);
  const definitionStyle = stepBody(styles.workplaceSpectrumDefinition);
  // The "Your Answer" label + answer pill stay full-size on mobile (match Likert);
  // only reading prose below steps down.
  const yourAnswerLabelStyle = styles.workplaceSpectrumResultsYourAnswerLabel;
  const answerLabelStyle = stepBody(styles.workplaceSpectrumResultsAnswerLabel);
  const chartTitleStyle = stepBody(styles.workplaceSpectrumResultsChartTitle);
  const chartResponsesStyle = stepBody(styles.workplaceSpectrumResultsChartResponses);
  const explanationStyle: React.CSSProperties = stepBody(
    isChartsStacked
      ? { ...styles.workplaceSpectrumResultsExplanation, flex: '1 1 auto', width: '100%', alignSelf: 'stretch' }
      : styles.workplaceSpectrumResultsExplanation,
  );

  const getLabelAriaLabel = useCallback(
    (color: SpectrumColor, label: string): string => {
      const subtitle = getColorSubtitle(color);
      return subtitle ? `${label} ${subtitle}` : label;
    },
    [getColorSubtitle],
  );

  return (
    <div
      className={className}
      ref={containerRef}
      style={{
        ...styles.workplaceSpectrumContainer,
        width: interactionWidth,
        maxWidth: '100%',
        margin: '0 auto',
        ...style,
      }}
      data-testid="workplace-spectrum-container"
    >
      <style>{contentResetStyles}</style>

      <div style={styles.workplaceSpectrumHeader}>
        {React.createElement(titleLevel, { style: titleStyle }, titleText)}
        <RichTextContent
          className="spectrum-definition-content"
          style={subtitleStyle}
          html={descriptionHtml}
        />
      </div>

      {priorAnswerStatus === 'loading' ? (
        <ResponseThresholdEmptyState variant="checking-answer" />
      ) : (
        <>
          {isQuestionStageVisible ? (
            <div style={questionStageStyle} data-testid="workplace-spectrum-question-stage">
              <div style={{ ...styles.workplaceSpectrumCard, backgroundColor: cardBackground }}>
                <div style={styles.workplaceSpectrumGaugeArea}>
                  <svg
                    viewBox="0 0 560 320"
                    role="presentation"
                    aria-hidden="true"
                    style={styles.workplaceSpectrumGaugeSvg}
                  >
                    <defs>
                      <clipPath id="pointer-base-clip">
                        <rect x={0} y={0} width={560} height={QUESTION_CENTER_Y} />
                      </clipPath>
                    </defs>
                    <line
                      x1={QUESTION_CENTER_X - QUESTION_OUTER_RADIUS}
                      x2={QUESTION_CENTER_X + QUESTION_OUTER_RADIUS}
                      y1={QUESTION_CENTER_Y}
                      y2={QUESTION_CENTER_Y}
                      stroke="#000000"
                      strokeWidth={1.5}
                    />
                    {SEGMENT_GEOMETRY.map((segment) => (
                      <path
                        key={segment.color}
                        d={buildSegmentPath(segment)}
                        fill={colorMeta[segment.color].fill}
                        stroke="#000000"
                        strokeWidth={1.5}
                        onMouseEnter={() => setHovered(segment.color)}
                        onMouseLeave={() => setHovered(null)}
                        onClick={() => setSelected(segment.color)}
                        style={styles.workplaceSpectrumSegment}
                      />
                    ))}
                    <g clipPath="url(#pointer-base-clip)">
                      <path
                        data-testid="workplace-spectrum-pointer"
                        d={pointerPath}
                        fill="#252848"
                        pointerEvents="none"
                      />
                      <circle
                        cx={QUESTION_CENTER_X}
                        cy={QUESTION_CENTER_Y}
                        r={18}
                        fill="#252848"
                        pointerEvents="none"
                      />
                    </g>
                  </svg>

                  {SEGMENT_GEOMETRY.map((segment) => {
                    const shouldDim =
                      shouldDimUnselectedLabels &&
                      focusedLabelColor !== null &&
                      focusedLabelColor !== segment.color;

                    return (
                      <button
                        key={`label-${segment.color}`}
                        type="button"
                        aria-label={getLabelAriaLabel(segment.color, colorMeta[segment.color].label)}
                        aria-pressed={selectedColor === segment.color}
                        onMouseEnter={() => setHovered(segment.color)}
                        onMouseLeave={() => setHovered(null)}
                        onFocus={() => setHovered(segment.color)}
                        onBlur={() => setHovered(null)}
                        onClick={() => setSelected(segment.color)}
                        style={{
                          ...styles.workplaceSpectrumLabelButton,
                          ...getLabelPositionStyles(segment),
                          ...(shouldDim ? styles.workplaceSpectrumLabelButtonMuted : {}),
                        }}
                      >
                        <span style={labelTitleStyle}>{colorMeta[segment.color].label}</span>
                        {getColorSubtitle(segment.color) ? (
                          <span style={labelSubtitleStyle}>
                            {getColorSubtitle(segment.color)}
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>

                <div
                  style={{
                    ...styles.workplaceSpectrumDefinitionArea,
                    display: 'grid',
                    minHeight: undefined,
                    alignItems: 'start',
                    justifyContent: 'start',
                  }}
                  data-testid="workplace-spectrum-definition-area"
                >
                  <div
                    style={{
                      gridRow: '1 / 2',
                      gridColumn: '1 / 2',
                      alignSelf: 'stretch',
                      visibility: activeColor === null ? 'visible' : 'hidden',
                      maxWidth: '62ch',
                      margin: '0 auto',
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      paddingBottom: '40px',
                    }}
                    data-testid={activeColor === null ? 'workplace-spectrum-definition-neutral' : undefined}
                  >
                    <div
                      className="spectrum-definition-content"
                      style={definitionStyle}
                    >
                      <p>{t('workplace_color_spectrum.gauge_instruction')}</p>
                    </div>
                  </div>
                  {SEGMENT_GEOMETRY.map((segment) => {
                    const isActive = activeColor === segment.color;
                    const definitionText = getColorDefinition(segment.color);

                    return (
                      <div
                        key={segment.color}
                        style={{
                          gridRow: '1 / 2',
                          gridColumn: '1 / 2',
                          visibility: isActive ? 'visible' : 'hidden',
                          maxWidth: '62ch',
                          margin: '0 auto',
                          width: '100%',
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'flex-start',
                        }}
                        data-testid={isActive ? 'workplace-spectrum-definition' : undefined}
                      >
                        <div
                          className="spectrum-definition-content"
                          style={definitionStyle}
                        >
                          <p>{definitionText}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <SubmitRevealButton
                disabled={submitDisabled}
                isSubmitting={isSubmitting}
                prefersReducedMotion={prefersReducedMotion}
                onSubmit={handleSubmit}
                baseStyle={styles.workplaceSpectrumSubmitButton}
                disabledStyle={styles.workplaceSpectrumSubmitButtonDisabled}
                hoverStyle={styles.actionButtonHover}
                accentColor={submitAccentColor}
                submittingTextColor={submitStatusTextColor}
                data-testid="workplace-spectrum-submit-button"
              />
            </div>
          ) : null}

          {shouldShowResults && resultsHidden ? (
            <div style={resultsStageStyle} data-testid="workplace-spectrum-results-stage">
              <ThankYouMessage visible />
            </div>
          ) : null}

          {shouldShowResults && !resultsHidden ? (
            <div
              style={resultsStageStyle}
              data-testid="workplace-spectrum-results-stage"
            >
              <div
                style={{ ...styles.workplaceSpectrumResultsHeaderRow, marginBottom: '21px' }}
                data-testid="workplace-spectrum-your-answer"
              >
                <span style={yourAnswerLabelStyle}>
                  {t('results.your_answer')}
                </span>
                <div
                  style={{
                    ...styles.workplaceSpectrumResultsYourAnswerPill,
                    backgroundColor: colorMeta[submittedColor].fill,
                    ...(submittedColor === 'yellow' && sectionSurface === 'dark'
                      ? styles.workplaceSpectrumResultsYourAnswerPillBordered
                      : null),
                  }}
                >
                  <span
                    style={{
                      ...styles.workplaceSpectrumResultsYourAnswerPillText,
                      ...(submittedColor === 'yellow'
                        ? styles.workplaceSpectrumResultsYourAnswerPillTextDark
                        : null),
                    }}
                  >
                    {colorMeta[submittedColor].label}
                  </span>
                </div>
              </div>

              <div
                style={resultsCardStyle}
                data-testid="workplace-spectrum-results"
              >
                <div style={correctAnswerClusterStyle}>
                  <div
                    style={styles.workplaceSpectrumResultsAnswerTile}
                    data-testid="workplace-spectrum-correct-answer"
                  >
                    <div
                      style={{
                        ...styles.workplaceSpectrumResultsAnswerBar,
                        backgroundColor: colorMeta[correctAnswer].fill,
                      }}
                    >
                      <img
                        src={workplaceColorSpectrumEmojiAssets[correctAnswer]}
                        alt={t('workplace_color_spectrum.emoji_alt', {
                          color: colorMeta[correctAnswer].label,
                        })}
                        style={styles.workplaceSpectrumResultsEmoji}
                      />
                    </div>
                    <div style={answerLabelStyle}>
                      {t('results.correct_answer', { answer: colorMeta[correctAnswer].label })}
                    </div>
                  </div>

                  {resultExplanationHtml ? (
                    <RichTextContent
                      className="spectrum-definition-content"
                      style={explanationStyle}
                      data-testid="workplace-spectrum-results-explanation"
                      html={resultExplanationHtml}
                    />
                  ) : null}
                </div>

                <div style={chartsRowStyle}>
                  {isBelowResponseThreshold || isResultsPending ? (
                    <ResponseThresholdEmptyState variant={isResultsPending ? 'loading' : 'empty'} />
                  ) : resultsDistributions.map((distribution) => {
                    // Final (fully revealed) geometry; applyChartProgress rewinds
                    // and animates it imperatively via the element refs.
                    const chartSegments = buildHalfDonutSegments(distribution.values, colorMeta, 1);
                    const baselineStartX = RESULTS_CHART_CENTER_X - RESULTS_CHART_OUTER_RADIUS;
                    const baselineEndX = baselineStartX + RESULTS_CHART_OUTER_RADIUS * 2;

                    return (
                      <div
                        key={distribution.id}
                        style={chartCardStyle}
                        data-testid={`workplace-spectrum-chart-${distribution.id}`}
                      >
                        <div style={styles.workplaceSpectrumResultsChartHeader}>
                          <span style={chartTitleStyle}>
                            {distribution.title}
                          </span>
                          <span style={chartResponsesStyle}>
                            {distribution.responsesLabel}
                          </span>
                        </div>
                        <svg
                          viewBox={`0 0 ${RESULTS_CHART_WIDTH} ${RESULTS_CHART_HEIGHT}`}
                          role="img"
                          aria-label={t('results.distribution_chart', { title: distribution.title })}
                          style={styles.workplaceSpectrumResultsChartSvg}
                        >
                          {chartSegments.map((segment) => (
                            <path
                              key={segment.color}
                              ref={(element) => {
                                chartPathRefs.current.set(`${distribution.id}:${segment.color}`, element);
                              }}
                              d={buildHalfDonutPath(segment)}
                              fill={segment.fill}
                              stroke="#000000"
                              strokeWidth={1}
                            />
                          ))}
                          <line
                            ref={(element) => {
                              chartBaselineRefs.current.set(distribution.id, element);
                            }}
                            x1={baselineStartX}
                            x2={baselineEndX}
                            y1={RESULTS_CHART_CENTER_Y}
                            y2={RESULTS_CHART_CENTER_Y}
                            stroke="#000000"
                            strokeWidth={1}
                            data-testid={`workplace-spectrum-chart-${distribution.id}-baseline`}
                          />
                        </svg>

                        <div style={styles.workplaceSpectrumResultsLegend}>
                          {COLOR_KEYS.map((color) => (
                            <div key={color} style={styles.workplaceSpectrumResultsLegendItem}>
                              <span style={legendLabelStyle}>
                                {colorMeta[color].label}
                              </span>
                              <span
                                style={{
                                  ...legendValueStyle,
                                  color: colorMeta[color].legendFill,
                                }}
                              >
                                {distribution.values[color]}%
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

WorkplaceColorSpectrumRenderer.displayName = 'WorkplaceColorSpectrumRenderer';
